import React, { useState, useEffect, useCallback } from 'react';
import { getHistoryByDate, getAllDates, deleteMeal, updateMeal, loadHistory as loadHistoryFromServer } from './storage';
import { saveMealToHistory, analyzeMeal } from './api';
import { detectMealType, mealNormsByType } from './mealNorms';
import { useTheme } from './ThemeContext';
import { Icons } from './icons';

function DayStats({ dailyNorms, userProfile, onBack }) {
  const { colors } = useTheme();
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('ru-RU'));
  const [availableDates, setAvailableDates] = useState([]);
  const [meals, setMeals] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, fat: 0, carbs: 0 });
  // Id приёма, чья кнопка копирования сейчас подсвечена (визуальная обратная связь после успешного копирования)
  const [copiedMealId, setCopiedMealId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editMeal, setEditMeal] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [addMealDate, setAddMealDate] = useState(new Date().toISOString().split('T')[0]);
  const [addMealTime, setAddMealTime] = useState(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
  const [addMealText, setAddMealText] = useState('');
  // Флаг блокировки кнопки на время AI-анализа и сохранения (защита от задваивания при повторном клике)
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);

  // Загружаем историю с сервера при монтировании, чтобы обновить кэш
  useEffect(() => {
    const init = async () => {
      await loadHistoryFromServer();
      loadDates();
    };
    init();
  }, []);

  // loadMealsForDate — чистая функция чтения из локального кэша (см. ниже).
  // Объявлена через useCallback, чтобы её можно было указать в зависимостях useEffect,
  // не пересоздавая её на каждый рендер.
  const loadMealsForDate = useCallback((date) => {
    const dayMeals = getHistoryByDate(date); setMeals(dayMeals);
    let tc = 0, tp = 0, tf = 0, tcb = 0;
    dayMeals.forEach(meal => {
      tc += getMealValue(meal, 'calories');
      tp += getMealValue(meal, 'protein');
      tf += getMealValue(meal, 'fat');
      tcb += getMealValue(meal, 'carbs');
    });
    setTotals({ calories: Math.round(tc*10)/10, protein: Math.round(tp*10)/10, fat: Math.round(tf*10)/10, carbs: Math.round(tcb*10)/10 });
  }, []);

  useEffect(() => { loadMealsForDate(selectedDate); }, [selectedDate, loadMealsForDate]);

  const loadDates = () => { const dates = getAllDates(); setAvailableDates(dates); };

  // Извлекает число из meal: сначала из отдельного поля, потом парсит mealText
  const getMealValue = (meal, field) => {
    if (meal[field] !== undefined && meal[field] !== null && meal[field] !== '') {
      return parseFloat(meal[field]) || 0;
    }
    // Fallback — парсим из mealText (AI-формат)
    const d = meal.mealText || '';
    const patterns = {
      calories: /Калории:\s*([\d.]+)\s*ккал/,
      protein: /Белки:\s*([\d.]+)\s*г/,
      fat: /Жиры:\s*([\d.]+)\s*г/,
      carbs: /Углеводы:\s*([\d.]+)\s*г/
    };
    const m = d.match(patterns[field]);
    return m ? parseFloat(m[1]) || 0 : 0;
  };

  const handleDeleteMeal = (id) => { setDeleteConfirmId(id); };

  // Убирает «?» из продукта без веса: в текст для правки/копирования он не нужен.
  const cleanProduct = (p) => p.replace(/\s*\?\s*$/, '').replace(/\s+/g, ' ').trim();

  // Текст приёма для копирования и для окна правки — единый формат:
  // только продукты с измерениями, через запятую (как при копировании).
  // Если продуктов не разобрать — отдаём исходный текст, чтобы поле не осталось пустым.
  const mealProductsText = (meal) => {
    const list = parseMealText(meal.mealText).products.map(cleanProduct).filter(Boolean);
    if (list.length > 0) return list.join(', ');
    const fallback = (meal.originalText || meal.mealText || '').trim();
    return fallback;
  };

  // Копирование приёма: собирает список продуктов с граммовкой из mealText в системный буфер
  const handleCopyMeal = async (meal) => {
    const text = mealProductsText(meal);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      // Успешно — на короткое время подсвечиваем иконку, потом возвращаем (меняется только эта кнопка)
      setCopiedMealId(meal.id);
      setTimeout(() => setCopiedMealId((cur) => (cur === meal.id ? null : cur)), 600);
    } catch (e) {
      // Молча при неудаче (кнопка не меняется); в ряде браузеров буфер требует фокуса/разрешения
      console.error('Копирование не удалось:', e);
    }
  };

  const parseMealType = (data) => { const m = data.match(/Тип приёма:\s*(.+)/); return m ? m[1] : 'Приём пищи'; };

  // Формат ответа AI менялся (дефис → без дефиса), поэтому список продуктов читается в обоих видах.
  const parseMealText = (data) => {
    const out = { products: [], noWeight: [] };
    if (!data) return out;
    const lines = data.split('\n');
    const pi = lines.findIndex(l => l.startsWith('Продукты:'));
    if (pi === -1) return out;
    const isService = (l) => l === '' || /^(Калории|Белки|Жиры|Углеводы|Соотношение|Не распознано|Не указан вес|Без веса|Итого)/.test(l) || l.startsWith('⚠');
    // Строки «Не распознано / Не указан вес / Без веса» в карточке не показываем:
    // продукт без веса и так виден в списке со знаком «?» (в расчёт он не идёт) — решение владельца 2026-09-17.
    for (let i = pi + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (isService(line)) continue;
      const product = line.replace(/^[-•*]\s*/, '').trim();
      if (product) out.products.push(product);
    }
    return out;
  };

  const parseMacros = (data) => {
    const pm = data.match(/Белки:\s*([\d.]+)\s*г/);
    const fm = data.match(/Жиры:\s*([\d.]+)\s*г/);
    const cm = data.match(/Углеводы:\s*([\d.]+)\s*г/);
    const rm = data.match(/Соотношение Б:Ж:У\s*[:=]\s*([\d.]+)[^\d]*([\d.]+)[^\d]*([\d.]+)/);
    return { protein: pm ? pm[1] : '0', fat: fm ? fm[1] : '0', carbs: cm ? cm[1] : '0', ratio: rm ? `${rm[1]}/${rm[2]}/${rm[3]}` : '0/0/0' };
  };

  const getProgressPercent = (c, t) => t ? Math.min((c/t)*100, 100) : 0;
  const toInputDate = (ds) => { const p = ds.split('.'); return `${p[2]}-${p[1].padStart(2,'0')}-${p[0].padStart(2,'0')}`; };
  const fromInputDate = (id) => { const p = id.split('-'); return `${p[2]}.${p[1]}.${p[0]}`; };
  const formatDate = (ds) => { const p = ds.split('.'); const d = new Date(p[2], p[1]-1, p[0]); return d.toLocaleDateString('ru-RU', { weekday:'long', day:'numeric', month:'long' }); };

  const s = {
    page: { maxWidth:'420px', margin:'0 auto', minHeight:'100vh', background:colors.page, fontFamily:'system-ui, -apple-system, sans-serif', overflowX:'hidden' },
    header: { background:colors.header, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' },
    backBtn: { background:'none', border:'none', color:'#fff', fontSize:'24px', cursor:'pointer', padding:'0 8px' },
    headerTitle: { fontSize:'18px', fontWeight:'600', color:'#fff' }, headerPlaceholder: { width:'40px' },
    dateSelector: { padding:'16px 20px', background:colors.card, borderBottom:`1px solid ${colors.border}` },
    dateInput: { width:'100%', padding:'12px 16px', fontSize:'16px', border:`1px solid ${colors.inputBorder}`, borderRadius:'12px', background:colors.inputBg, color:colors.text, marginBottom:'8px', cursor:'pointer', boxSizing:'border-box', WebkitAppearance:'none', MozAppearance:'none', appearance:'none' },
    dateInfo: { fontSize:'14px', color:colors.textMuted, textAlign:'center' },
    emptyState: { padding:'60px 20px', textAlign:'center' }, emptyIcon: { fontSize:'64px', marginBottom:'20px' },
    emptyText: { fontSize:'18px', fontWeight:'600', color:colors.textSecondary, marginBottom:'8px' },
    emptySubtext: { fontSize:'14px', color:colors.textMuted },
    totalsCard: { margin:'16px 20px', padding:'20px', background:colors.card, borderRadius:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' },
    totalsTitle: { fontSize:'12px', fontWeight:'600', color:colors.textMuted, marginBottom:'12px', letterSpacing:'0.5px' },
    caloriesRow: { display:'flex', alignItems:'baseline', gap:'8px', marginBottom:'8px' },
    caloriesValue: { fontSize:'36px', fontWeight:'700', color:colors.text },
    caloriesTarget: { fontSize:'14px', color:colors.textMuted },
    progressBar: { height:'6px', background:colors.progressBg, borderRadius:'3px', marginBottom:'4px' },
    progressFill: { height:'100%', borderRadius:'3px', transition:'width 0.3s' },
    progressPercent: { fontSize:'12px', color:colors.textMuted, textAlign:'right' },
    macrosGrid: { marginTop:'16px', display:'flex', flexDirection:'column', gap:'10px' },
    macroItem: { display:'flex', flexDirection:'column', gap:'4px' },
    macroIcon: { fontSize:'16px', marginRight:'4px' }, macroName: { fontSize:'12px', color:colors.textMuted, marginBottom:'2px' },
    macroCurrent: { fontSize:'16px', fontWeight:'600', color:colors.text },
    macroTarget: { fontSize:'12px', color:colors.textMuted },
    macroBar: { height:'4px', background:colors.progressBg, borderRadius:'2px', marginTop:'4px' },
    macroBarFill: { height:'100%', borderRadius:'2px', transition:'width 0.3s' },
    mealsList: { margin:'16px 20px' },
    mealsListTitle: { fontSize:'14px', fontWeight:'600', color:colors.textSecondary, marginBottom:'12px' },
    mealCard: { padding:'16px', background:colors.card, borderRadius:'16px', marginBottom:'12px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' },
    mealHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' },
    mealType: { fontSize:'16px', fontWeight:'600', color:'#1D9E75' },
    mealTime: { fontSize:'13px', color:colors.textMuted },
    mealTopRow: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' },
    mealCalories: { fontSize:'18px', fontWeight:'700', color:colors.text },
    mealRatio: { fontSize:'12px', color:colors.textMuted, background:colors.progressBg, padding:'4px 8px', borderRadius:'6px' },
    mealContent: { display:'flex', gap:'16px', fontSize:'13px' },
    mealProducts: { flex:1, color:colors.text, whiteSpace:'pre-wrap', wordBreak:'break-word' }, mealMacros: { display:'flex', flexDirection:'column', gap:'4px' },
    macroLine: { color:colors.textLight }, productItem: { marginBottom:'4px' },
    addMealBtn: { width:'100%', padding:'12px', marginBottom:'12px', background:'#1D9E75', color:'white', border:'none', borderRadius:'12px', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
    inputRow: { display:'flex', gap:8, marginBottom:12 },
    smallInput: { flex:1, padding:'10px 8px', fontSize:13, border:`1px solid ${colors.inputBorder}`, borderRadius:10, background:colors.inputBg, color:colors.text, textAlign:'center', boxSizing:'border-box', width:0 },
    labelSmall: { fontSize:11, color:colors.textMuted, marginBottom:2, textAlign:'center' }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}><Icons.back size={24} color="#fff" /></button>
        <div style={s.headerTitle}><Icons.stats size={18} color="#fff" /> Статистика дня</div>
        <div style={s.headerPlaceholder}></div>
      </div>
      <div style={s.dateSelector}>
        <input type="date" style={s.dateInput} value={toInputDate(selectedDate)} onChange={e => setSelectedDate(fromInputDate(e.target.value))} max={toInputDate(new Date().toLocaleDateString('ru-RU'))} />
        <div style={s.dateInfo}>{availableDates.includes(selectedDate) ? formatDate(selectedDate) : `${formatDate(selectedDate)} • Нет приёмов`}</div>
      </div>
      {meals.length === 0 ? (
        <div style={s.emptyState}>
          <div style={s.emptyIcon}><Icons.meal size={64} color={colors.textMuted} /></div>
          <div style={s.emptyText}>Нет приёмов за этот день</div>
          <div style={s.emptySubtext}>Добавьте приёмы через кнопку «+ Добавить приём»</div>
          <button style={{...s.addMealBtn, marginTop:16, width:'auto', padding:'12px 24px'}} onClick={() => { setShowAddMeal(true); }}>+ Добавить приём</button>
        </div>
      ) : (
        <>
          <div style={s.totalsCard}>
            <div style={s.totalsTitle}>ИТОГО ЗА ДЕНЬ</div>
            <div style={s.caloriesRow}><div style={s.caloriesValue}>{totals.calories}</div><div style={s.caloriesTarget}>из {dailyNorms.calories} ккал</div></div>
            <div style={s.progressBar}><div style={{...s.progressFill, width:`${getProgressPercent(totals.calories, dailyNorms.calories)}%`, background: totals.calories > dailyNorms.calories ? '#e53935' : '#1D9E75'}} /></div>
            <div style={s.progressPercent}>{Math.round((totals.calories / dailyNorms.calories) * 100)}%</div>
            <div style={s.macrosGrid}>
              {[{ icon:<Icons.protein size={16} color="#5DCAA5" />, name:'Белки', cur:totals.protein, tgt:dailyNorms.protein, unit:'г', color:'#5DCAA5'},
                { icon:<Icons.fat size={16} color="#FAC775" />, name:'Жиры', cur:totals.fat, tgt:dailyNorms.fat, unit:'г', color:'#FAC775'},
                { icon:<Icons.carbs size={16} color="#85B7EB" />, name:'Углеводы', cur:totals.carbs, tgt:dailyNorms.carbs, unit:'г', color:'#85B7EB'}].map(m => (
                <div key={m.name} style={s.macroItem}>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    {m.icon}<div style={s.macroName}>{m.name}</div>
                  </div>
                  <div style={{display:'flex', justifyContent:'space-between'}}>
                    <div style={s.macroCurrent}>{m.cur} {m.unit}</div><div style={s.macroTarget}>из {m.tgt} {m.unit}</div>
                  </div>
                  <div style={s.macroBar}><div style={{...s.macroBarFill, width:`${getProgressPercent(m.cur, m.tgt)}%`, background:m.color}} /></div>
                </div>
              ))}
            </div>
          </div>
          <div style={s.mealsList}>
            <div style={s.mealsListTitle}>Приёмы пищи ({meals.length})</div>
            <button style={s.addMealBtn} onClick={() => setShowAddMeal(true)}>+ Добавить приём</button>
            {meals.sort((a,b) => b.timestamp.localeCompare(a.timestamp)).map(meal => {
              const cal = getMealValue(meal, 'calories');
              const prot = getMealValue(meal, 'protein');
              const fat = getMealValue(meal, 'fat');
              const carb = getMealValue(meal, 'carbs');
              const hasMacros = cal > 0 || prot > 0 || fat > 0 || carb > 0;
              const parsed = parseMealText(meal.mealText);
              const products = parsed.products;
              const macros = parseMacros(meal.mealText);
              return (
                <div key={meal.id} style={s.mealCard}>
                  <div style={s.mealHeader}>
                    <div style={s.mealType}>{parseMealType(meal.mealText)}</div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <button
                        onClick={() => handleCopyMeal(meal)}
                        title="Копировать"
                        style={{
                          background: copiedMealId === meal.id ? colors.success : 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          margin: -4,
                          borderRadius: 6,
                          transform: copiedMealId === meal.id ? 'scale(1.15)' : 'scale(1)',
                          transition: 'background 0.2s, transform 0.2s',
                        }}
                      >
                        <Icons.copy size={14} color={copiedMealId === meal.id ? colors.successText : colors.textMuted} />
                      </button>
                      <button style={{background:'none',border:'none',cursor:'pointer',padding:0}} onClick={() => {
                        const [d,m,y] = (meal.date || '').split('.');
                        setEditMeal(meal);
                        // В окне правки — только продукты с измерениями (как при копировании),
                        // а не служебный вывод AI и не исходная диктовка.
                        setEditText(mealProductsText(meal));
                        setEditDate(y && m && d ? `${y}-${m}-${d}` : new Date().toISOString().split('T')[0]);
                        setEditTime(meal.time || '');
                      }}><Icons.edit size={14} color={colors.textMuted} /></button>
                      <button style={{background:'none',border:'none',cursor:'pointer',padding:0}} onClick={() => handleDeleteMeal(meal.id)}><Icons.delete size={14} color={colors.textMuted} /></button>
                      <div style={s.mealTime}>{meal.time}</div>
                    </div>
                  </div>
                  <div style={s.mealTopRow}>
                    <div style={s.mealCalories}>{cal > 0 ? `${cal} ккал` : macros.protein !== '0' ? `${parseCalories(meal.mealText)} ккал` : '?'}</div>
                    {hasMacros && <div style={s.mealRatio}>Б:Ж:У = {(() => { const pk = prot*4, fk = fat*9, ck = carb*4, t = pk+fk+ck; return t ? `${Math.round(pk/t*100)}/${Math.round(fk/t*100)}/${Math.round(ck/t*100)}` : '0/0/0'; })()}</div>}
                  </div>
                  <div style={s.mealContent}>
                    <div style={s.mealProducts}>
                      {products.length > 0 ? products.map((p,i) => <div key={i} style={s.productItem}>• {p}</div>) : <div style={{color:colors.textMuted, fontSize:12}}>Приём не разобран — состав не распознан</div>}
                    </div>
                    {hasMacros && (
                      <div style={s.mealMacros}>
                        <div style={s.macroLine}>Белки: {prot} г</div>
                        <div style={s.macroLine}>Жиры: {fat} г</div>
                        <div style={s.macroLine}>Углеводы: {carb} г</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {deleteConfirmId && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={() => setDeleteConfirmId(null)}>
          <div style={{background:colors.card,borderRadius:20,padding:24,width:'90%',maxWidth:340,textAlign:'center'}} onClick={e => e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:600,color:colors.text,marginBottom:16}}>Удалить этот приём?</div>
            <div style={{display:'flex',gap:10}}>
              <button style={{flex:1,padding:12,background:'#e53935',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:500,cursor:'pointer'}} onClick={async () => { await deleteMeal(deleteConfirmId); setDeleteConfirmId(null); loadDates(); loadMealsForDate(selectedDate); }}>Удалить</button>
              <button style={{flex:1,padding:12,background:colors.border,color:colors.text,border:'none',borderRadius:12,fontSize:14,fontWeight:500,cursor:'pointer'}} onClick={() => setDeleteConfirmId(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
      {showAddMeal && (
            <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={() => { if (!adding) setShowAddMeal(false); }}>
              <div style={{background:colors.card,borderRadius:20,padding:24,width:'90%',maxWidth:400}} onClick={e => e.stopPropagation()}>
                <div style={{fontSize:16,fontWeight:600,color:colors.text,marginBottom:16}}>Добавить приём</div>
                <div style={{display:'flex',gap:10,marginBottom:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:colors.textMuted,marginBottom:4}}>Дата</div>
                    <input type="date" value={addMealDate} onChange={e => setAddMealDate(e.target.value)} max={new Date().toISOString().split('T')[0]} disabled={adding} style={{width:'100%',padding:'10px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:colors.textMuted,marginBottom:4}}>Время</div>
                    <input type="time" value={addMealTime} onChange={e => setAddMealTime(e.target.value)} disabled={adding} style={{width:'100%',padding:'10px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
                  </div>
                </div>
                <textarea value={addMealText} onChange={e => setAddMealText(e.target.value)} placeholder="Что съел(а)? Например: завтрак — овсянка 200 г, яйцо, кофе" disabled={adding} style={{width:'100%',padding:12,fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,minHeight:80,resize:'vertical',boxSizing:'border-box',marginBottom:12}} />
                  <div style={{display:'flex',gap:10}}>
                    <button style={{flex:1,padding:12,background:colors.card,color:colors.text,border:`1px solid ${colors.border}`,borderRadius:12,fontSize:14,fontWeight:500,cursor: adding ? 'not-allowed' : 'pointer',opacity: adding ? 0.6 : 1}} disabled={adding} onClick={async () => {
                    if (adding) return;
                    setAdding(true);
                    try {
                    if (!addMealText.trim()) { alert('Введите текст приёма'); return; }

                    // Получаем auth
                    const savedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
                    if (!savedUser.login || !savedUser.token) { alert('Добавление: нет авторизации'); return; }

                    // Анализируем через AI
                    // Считаем норму на этот приём (в % от дневной) и передаём через userParams.mealNorms
                    const mealType = detectMealType(addMealText);
                    const mealNorm = mealNormsByType(dailyNorms, mealType);
                    const userParams = { mealNorms: mealNorm };
                    const aiResult = await analyzeMeal(savedUser.login, savedUser.token, addMealText.trim(), userParams, { mealNorms: mealNorm });
                    if (!aiResult.success) { alert('Добавление: AI ошибка — ' + JSON.stringify(aiResult)); setAdding(false); return; }

                    const [y,m,d] = addMealDate.split('-');
                    const ruDate = `${d}.${m}.${y}`;
                    const dateTimeStr = `${addMealDate}T${addMealTime}:00`;
                    const newMeal = {
                      id: String(Date.now()),
                      timestamp: dateTimeStr,
                      date: ruDate,
                      time: addMealTime,
                      mealText: aiResult.result,
                      originalText: addMealText.trim()
                    };
                    await saveMealToHistory(savedUser.login, savedUser.token, newMeal);
                    // Обновляем кэш с сервера, после чего setSelectedDate
                    // вызовет useEffect → loadMealsForDate для новой даты
                    await loadHistoryFromServer();
                    setShowAddMeal(false);
                    setAddMealDate(new Date().toISOString().split('T')[0]);
                    setAddMealTime(new Date().toLocaleTimeString('ru-RU', { hour:'2-digit', minute:'2-digit' }));
                    setAddMealText('');
                    loadDates();
                    loadMealsForDate(ruDate);
                    setAdding(false);
                    } catch (e) { setAdding(false); alert('Ошибка: ' + e.message); console.error(e); }
                  }}>{adding ? 'Обработка…' : 'Анализировать и сохранить'}</button>
                  <button style={{flex:1,padding:12,background:colors.border,color:colors.text,border:'none',borderRadius:12,fontSize:14,fontWeight:500,cursor: adding ? 'not-allowed' : 'pointer',opacity: adding ? 0.5 : 1}} disabled={adding} onClick={() => setShowAddMeal(false)}>Отмена</button>
                </div>
              </div>
            </div>
          )}
      {editMeal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={() => { if (!editing) setEditMeal(null); }}>
          <div style={{background:colors.card,borderRadius:20,padding:24,width:'90%',maxWidth:400}} onClick={e => e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:600,color:colors.text,marginBottom:16}}>Редактировать приём</div>
            <div style={{display:'flex',gap:10,marginBottom:12}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:colors.textMuted,marginBottom:4}}>Дата</div>
                <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} max={new Date().toISOString().split('T')[0]} disabled={editing} style={{width:'100%',padding:'10px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:colors.textMuted,marginBottom:4}}>Время</div>
                <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} disabled={editing} style={{width:'100%',padding:'10px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
              </div>
            </div>
            <textarea value={editText} onChange={e => setEditText(e.target.value)} placeholder="Что съел(а)?" disabled={editing} style={{width:'100%',padding:12,fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,minHeight:80,resize:'vertical',boxSizing:'border-box',marginBottom:12}} />
            <div style={{display:'flex',gap:10}}>
              <button style={{flex:1,padding:12,background:'#1D9E75',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:500,cursor: editing ? 'not-allowed' : 'pointer',opacity: editing ? 0.6 : 1}} disabled={editing} onClick={async () => {
                if (editing) return;
                setEditing(true);
                try {
                  // Получаем auth
                  const savedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
                  if (!savedUser.login || !savedUser.token) { setEditing(false); return; }
                  // Анализируем через AI
                  const editMealType = detectMealType(editText);
                  const editMealNorm = mealNormsByType(dailyNorms, editMealType);
                  const editUserParams = { mealNorms: editMealNorm };
                  const aiResult = await analyzeMeal(savedUser.login, savedUser.token, editText, editUserParams, { mealNorms: editMealNorm });
                  if (!aiResult.success) { setEditing(false); return; }

                  const [y,m,d] = editDate.split('-');
                  const ruDate = `${d}.${m}.${y}`;
                  const dateTimeStr = `${editDate}T${editTime}:00`;

                  await updateMeal(editMeal.id, {
                    mealText: aiResult.result,
                    originalText: editText,
                    date: ruDate,
                    time: editTime,
                    timestamp: dateTimeStr
                  });
                  setEditMeal(null);
                  loadDates();
                  loadMealsForDate(ruDate);
                  setEditing(false);
                } catch (e) { setEditing(false); console.error(e); }
              }}>{editing ? 'Обработка…' : 'Анализировать и сохранить'}</button>
              <button style={{flex:1,padding:12,background:colors.border,color:colors.text,border:'none',borderRadius:12,fontSize:14,fontWeight:500,cursor: editing ? 'not-allowed' : 'pointer',opacity: editing ? 0.5 : 1}} disabled={editing} onClick={() => setEditMeal(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function parseCalories(data) {
  const m = (data || '').match(/Калории:\s*([\d.]+)\s*ккал/);
  return m ? m[1] : '?';
}

export default DayStats;
